import React, { useState, useEffect } from "react";
import { Table, Card, Tabs, Button, InputNumber, message, Space, Alert, Image, Typography, Row, Col, Divider } from "antd";
import axios from "axios";

const { Title, Text } = Typography;

const DetermineTimeGivenALearningCoefficient = ({
  fileId,
  fileName,
  setSelectedFile,
  initialConfig,
}) => {
  const [activeTab, setActiveTab] = useState("input");
  const [inputData, setInputData] = useState({
    x1: 1,
    Yx1: 0,
    N: 1,
    b: 0.8
  });
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const getAuthToken = () => localStorage.getItem("access_token");

  // Auth: Logout on 401
  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    window.location.reload();
  };

  useEffect(() => {
    if (fileId) {
      loadData(fileId);
    } else {
      setInputData({
        x1: initialConfig?.x1 || 1,
        Yx1: initialConfig?.Yx1 || 0,
        N: initialConfig?.N || 1,
        b: initialConfig?.b || 0.8
      });
      setResults(null);
      setIsSaved(false);
      setActiveTab("input");
    }
  }, [fileId, initialConfig]);

  const loadData = async (id) => {
    try {
      const token = getAuthToken();
      if (!token) return message.error("Not authenticated.");
      setIsLoading(true);

      const res = await axios.get(
        `/api/retrieve_determine_time_given_by_a_learning_coefficient/${id}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setInputData(res.data.input_data);
      setResults(res.data.output_data);
      setIsSaved(true);
      setActiveTab("output");
    } catch (err) {
      console.error("Load error:", err);
      if (err.response?.status === 401) handleLogout();
      message.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  const saveData = async () => {
    try {
      const token = getAuthToken();
      if (!token) return message.error("Not authenticated.");

      setIsLoading(true);

      const payload = {
        name: fileName || "Determine Time Given A Learning Coefficient",
        input_data: inputData,
      };

      let res;
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (fileId) {
        res = await axios.put(
          `/api/update_determine_time_given_by_a_learning_coefficient/${fileId}/`,
          payload,
          config
        );
      } else {
        res = await axios.post(
          `/api/save_determine_time_given_by_a_learning_coefficient/`,
          payload,
          config
        );
      }

      setResults(res.data.output_data);

      // Analyze the save response: Ensure the frontend updates the selectedFile.id immediately after saving to prevent duplicate entries.
      if (!fileId && setSelectedFile) {
        setSelectedFile({
          id: res.data.id,
          name: res.data.name,
          type: "learning_curve_determine_time",
          inputData: res.data.input_data
        });
      }

      setIsSaved(true);
      setActiveTab("output");
      message.success(fileId ? "Updated successfully" : "Saved successfully");
    } catch (err) {
      console.error("Save error:", err);
      if (err.response?.status === 401) handleLogout();
      message.error(`Failed to save: ${err.response?.data?.error || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, val) => {
    setInputData({ ...inputData, [field]: val });
  };

  const renderInputForm = () => (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Text strong>Base Unit Number (x1):</Text><br />
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              value={inputData.x1}
              onChange={(v) => handleInputChange("x1", v)}
            />
          </Col>
          <Col span={12}>
            <Text strong>Labor Time for Base Unit (Yx1):</Text><br />
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              value={inputData.Yx1}
              onChange={(v) => handleInputChange("Yx1", v)}
            />
          </Col>
          <Col span={12}>
            <Text strong>Last Unit Number (N):</Text><br />
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              value={inputData.N}
              onChange={(v) => handleInputChange("N", v)}
            />
          </Col>
          <Col span={12}>
            <Text strong>Learning Coefficient (b) [0 to 1]:</Text><br />
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              max={1}
              step={0.01}
              value={inputData.b}
              onChange={(v) => handleInputChange("b", v)}
            />
          </Col>
        </Row>
        <Button
          type="primary"
          onClick={saveData}
          loading={isLoading}
          style={{ marginTop: 24 }}
          block
        >
          {isSaved ? "Update Calculation" : "Calculate & Save"}
        </Button>
      </Space>
    </Card>
  );

  const renderOutputTable = () => {
    if (!results) {
      return (
        <Alert
          message="Results not available"
          description="Please enter input data and click Calculate."
          type="info"
          showIcon
        />
      );
    }

    const { calculated_parameters, unit_time_table } = results;

    const columns = [
      { title: "Unit #", dataIndex: "unit", key: "unit" },
      {
        title: "Production Time",
        dataIndex: "production_time",
        key: "production_time",
        render: (val) => val.toFixed(4)
      },
      {
        title: "Cumulative Time",
        dataIndex: "cumulative_time",
        key: "cumulative_time",
        render: (val) => val.toFixed(4)
      }
    ];

    return (
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Card title="Calculated Parameters">
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Text strong>Learning Exponent:</Text><br />
              <Text>{calculated_parameters?.learning_exponent?.toFixed(6)}</Text>
            </Col>
            <Col span={8}>
              <Text strong>Unit 1 Time:</Text><br />
              <Text>{calculated_parameters?.unit1_time?.toFixed(4)}</Text>
            </Col>
            <Col span={8}>
              <Text strong>Time for Last Unit (N):</Text><br />
              <Text>{calculated_parameters?.time_last_unit?.toFixed(4)}</Text>
            </Col>
            <Col span={12}>
              <Divider style={{ margin: '8px 0' }} />
              <Text strong>Cumulative Time (1 to N):</Text><br />
              <Title level={4} style={{ margin: 0 }}>{calculated_parameters?.cumulative_time_1_to_N?.toFixed(4)}</Title>
            </Col>
            <Col span={12}>
              <Divider style={{ margin: '8px 0' }} />
              <Text strong>Average Time (1 to N):</Text><br />
              <Title level={4} style={{ margin: 0 }}>{calculated_parameters?.average_time_1_to_N?.toFixed(4)}</Title>
            </Col>
          </Row>
        </Card>

        <Card title="Unit-by-Unit Production Time">
          <Table
            dataSource={unit_time_table}
            columns={columns}
            pagination={{ pageSize: 10 }}
            size="small"
            rowKey="unit"
          />
        </Card>

        {results.chart_url && (
          <Card title="Learning Curve Graph">
            <Image
              src={results.chart_url}
              alt="Learning Curve"
              style={{ width: '100%', maxWidth: '800px' }}
              fallback="https://via.placeholder.com/800x400?text=Learning+Curve+Graph"
            />
          </Card>
        )}
      </Space>
    );
  };

  return (
    <Card
      title={fileName || "Determine Time Given A Learning Coefficient"}
      style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "input",
            label: "Inputs",
            children: renderInputForm()
          },
          {
            key: "output",
            label: "Results / Graph",
            disabled: !results,
            children: renderOutputTable()
          }
        ]}
      />
    </Card>
  );
};

export default DetermineTimeGivenALearningCoefficient;
